/**
 * BootScene — Initial Phaser scene responsible for asset preloading.
 *
 * Responsibilities:
 * 1. Preload essential assets for the first map (tileset, sprites, backgrounds, audio)
 * 2. Display a child-friendly loading progress bar with colorful visuals
 * 3. Transition to MapSelectScene on completion
 * 4. Target: all initial assets loaded within 3s on 10 Mbps connection
 *
 * Requirements: 10.1, 10.2
 */

import Phaser from 'phaser';

/**
 * Asset manifest for initial boot loading.
 * Kept lightweight to meet the 3s target on 10 Mbps (~3.75 MB budget).
 */
export interface BootAssetManifest {
  images: Array<{ key: string; path: string }>;
  spritesheets: Array<{
    key: string;
    path: string;
    frameWidth: number;
    frameHeight: number;
  }>;
  audio: Array<{ key: string; path: string }>;
}

/**
 * Default asset manifest for the initial boot.
 * Loads all available overworld, UI, and audio assets.
 */
export const DEFAULT_BOOT_MANIFEST: BootAssetManifest = {
  images: [
    // --- Overworld: Character frames (individual poses) ---
    { key: 'char-idle', path: 'assets/overworld/frames/idle.png' },
    { key: 'char-walk1', path: 'assets/overworld/frames/walk1.png' },
    { key: 'char-walk2', path: 'assets/overworld/frames/walk2.png' },
    { key: 'char-walk3', path: 'assets/overworld/frames/walk3.png' },
    { key: 'char-walk4', path: 'assets/overworld/frames/walk4.png' },
    { key: 'char-jump', path: 'assets/overworld/frames/jump.png' },
    { key: 'char-fall', path: 'assets/overworld/frames/fall.png' },

    // --- Floresta Encantada ---
    { key: 'floresta-bg', path: 'assets/adventure/floresta/floresta-bg.png' },
    { key: 'floresta-ground', path: 'assets/adventure/floresta/floresta-ground.png' },
    { key: 'floresta-platform', path: 'assets/adventure/floresta/floresta-platform.png' },
    { key: 'floresta-caracol', path: 'assets/adventure/floresta/floresta-caracol.png' },
    { key: 'floresta-besouro', path: 'assets/adventure/floresta/floresta-besouro.png' },
    { key: 'floresta-caverna', path: 'assets/adventure/floresta/floresta-caverna.png' },
    { key: 'floresta-preview', path: 'assets/adventure/floresta/floresta-preview.png' },

    // --- Montanha Gelada ---
    { key: 'montanha-bg', path: 'assets/adventure/montanha/montanha-bg.png' },
    { key: 'montanha-ground', path: 'assets/adventure/montanha/montanha-ground.png' },
    { key: 'montanha-platform', path: 'assets/adventure/montanha/montanha-platform.png' },
    { key: 'montanha-pinguim', path: 'assets/adventure/montanha/montanha-pinguim.png' },
    { key: 'montanha-yeti', path: 'assets/adventure/montanha/montanha-yeti.png' },
    { key: 'montanha-caverna', path: 'assets/adventure/montanha/montanha-caverna.png' },
    { key: 'montanha-preview', path: 'assets/adventure/montanha/montanha-preview.png' },

    // --- Reino Encantado ---
    { key: 'reino-bg', path: 'assets/adventure/reino/reino-bg.png' },
    { key: 'reino-ground', path: 'assets/adventure/reino/reino-ground.png' },
    { key: 'reino-platform', path: 'assets/adventure/reino/reino-platform.png' },
    { key: 'reino-rato', path: 'assets/adventure/reino/reino-rato.png' },
    { key: 'reino-morcego', path: 'assets/adventure/reino/reino-morcego.png' },
    { key: 'reino-caverna', path: 'assets/adventure/reino/reino-caverna.png' },
    { key: 'reino-preview', path: 'assets/adventure/reino/reino-preview.png' },

    // --- Espaço Sideral ---
    { key: 'espaco-bg', path: 'assets/adventure/espaco/espaco-bg.png' },
    { key: 'espaco-ground', path: 'assets/adventure/espaco/espaco-ground.png' },
    { key: 'espaco-platform', path: 'assets/adventure/espaco/espaco-platform.png' },
    { key: 'espaco-asteroide', path: 'assets/adventure/espaco/espaco-asteroide.png' },
    { key: 'espaco-alien', path: 'assets/adventure/espaco/espaco-alien.png' },
    { key: 'espaco-caverna', path: 'assets/adventure/espaco/espaco-caverna.png' },
    { key: 'espaco-preview', path: 'assets/adventure/espaco/espaco-preview.png' },

    // --- Legacy overworld assets (kept for backwards compatibility) ---
    { key: 'forest-bg-full', path: 'assets/overworld/forest-background-full.png' },
    { key: 'forest-tiles', path: 'assets/overworld/forest-tiles.png' },
    { key: 'forest-preview', path: 'assets/overworld/forest-preview.png' },
    { key: 'forest-enemy-beetle', path: 'assets/overworld/forest-enemy-beetle.png' },
    { key: 'forest-enemy-snail', path: 'assets/overworld/forest-enemy-snail.png' },
    { key: 'ice-aurora-sky', path: 'assets/overworld/ice-aurora-sky.png' },
    { key: 'ice-cliffs', path: 'assets/overworld/ice-cliffs.png' },
    { key: 'ice-snow-peaks', path: 'assets/overworld/ice-snow-peaks.png' },
    { key: 'ice-mountain-tiles', path: 'assets/overworld/ice-mountain-tiles.png' },
    { key: 'mountain-preview', path: 'assets/overworld/mountain-preview.png' },
    { key: 'ice-enemy-penguin', path: 'assets/overworld/ice-enemy-penguin.png' },
    { key: 'ice-enemy-yeti', path: 'assets/overworld/ice-enemy-yeti.png' },

    // --- Overworld: Power-ups ---
    { key: 'powerup-fireball', path: 'assets/overworld/powerup-fireball.png' },
    { key: 'powerup-shield', path: 'assets/overworld/powerup-shield.png' },
    { key: 'powerup-spring', path: 'assets/overworld/powerup-spring.png' },
    { key: 'powerup-star', path: 'assets/overworld/powerup-star.png' },

    // --- Overworld: Triggers ---
    { key: 'trigger-cave', path: 'assets/overworld/trigger-cave.png.png' },
    { key: 'trigger-crystal', path: 'assets/overworld/trigger-crystal.png' },
    { key: 'trigger-magic-tree', path: 'assets/overworld/trigger-magic-tree.png' },
    { key: 'trigger-mushroom', path: 'assets/overworld/trigger-mushroom.png' },
    { key: 'trigger-portal', path: 'assets/overworld/trigger-portal.png' },
    { key: 'trigger-tunnel', path: 'assets/overworld/trigger-tunnel.png' },

    // --- UI ---
    { key: 'ui-button', path: 'assets/ui/button.png' },
    { key: 'ui-logo', path: 'assets/ui/logo.png' },
    { key: 'ui-padlock', path: 'assets/ui/padlock.png' },

    // --- Map Select & Character ---
    { key: 'map-select-bg', path: 'assets/adventure/map-select-bg.png' },
    { key: 'personagem-frente', path: 'assets/adventure/personagem-frente.png' },

    // --- Victory images (one per world) ---
    { key: 'floresta-encantada-vitoria', path: 'assets/adventure/floresta-encantada-vitoria.png' },
    { key: 'montanha-gelada-vitoria', path: 'assets/adventure/montanha-gelada-vitoria.png' },
    { key: 'reino-encantado-vitoria', path: 'assets/adventure/reino-encantado-vitoria.png' },
    { key: 'espaco-sideral-vitoria', path: 'assets/adventure/espaco-sideral-vitoria.png' },

    // --- Final conclusion ---
    { key: 'conclusao-final', path: 'assets/adventure/conclusao-final.png' },

    // --- Finish portal ---
    { key: 'portal-fim', path: 'assets/adventure/portal-fim.png' },

    // --- Collectible letter sprites (A-Z) ---
    ...Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map((letter) => ({
      key: `letter-${letter}`,
      path: `assets/adventure/collectibles/letter-${letter}.png`,
    })),

    // --- Collectible number sprites (1-9) ---
    ...Array.from({ length: 9 }, (_, i) => ({
      key: `number-${i + 1}`,
      path: `assets/adventure/collectibles/number-${i + 1}.png`,
    })),
  ],
  spritesheets: [],
  audio: [
    { key: 'click', path: 'audio/click.mp3' },
    { key: 'enemy-squish', path: 'audio/enemy-squish.mp3' },
    { key: 'forest-ambient', path: 'audio/forest-ambient.mp3' },
    { key: 'forest-intro', path: 'audio/forest-intro.mp3' },
    { key: 'ice-mountain-ambient', path: 'audio/ice-mountain-ambient.mp3' },
    { key: 'ice-mountain-intro', path: 'audio/ice-mountain-intro.mp3' },
    { key: 'locked', path: 'audio/locked.mp3' },
    { key: 'menu-music', path: 'audio/menu-music.mp3' },
    { key: 'powerup-collect', path: 'audio/powerup-collect.mp3' },
    { key: 'powerup-expire', path: 'audio/powerup-expire.mp3' },
    { key: 'sparkle', path: 'audio/sparkle.mp3' },
  ],
};

// --- Loading bar visual constants ---

/** Colors for the child-friendly loading bar */
const COLORS = {
  barBackground: 0x2d2d5e,
  barBorder: 0xffd700,
  barFill: 0x44cc88,
  barFillHighlight: 0x66eebb,
  text: '#ffffff',
  subText: '#ccccff',
  starColor: 0xffd700,
};

/** Dimensions and layout */
const LAYOUT = {
  barWidth: 400,
  barHeight: 40,
  barRadius: 20,
  borderWidth: 4,
  starSize: 16,
  starCount: 5,
};

export class BootScene extends Phaser.Scene {
  private progressBar: Phaser.GameObjects.Graphics | null = null;
  private progressBarBg: Phaser.GameObjects.Graphics | null = null;
  private loadingText: Phaser.GameObjects.Text | null = null;
  private percentText: Phaser.GameObjects.Text | null = null;
  private stars: Phaser.GameObjects.Graphics[] = [];
  private manifest: BootAssetManifest;

  constructor(manifest?: BootAssetManifest) {
    super({ key: 'BootScene' });
    this.manifest = manifest ?? DEFAULT_BOOT_MANIFEST;
  }

  // --- Phaser lifecycle ---

  preload(): void {
    this.createLoadingUI();
    this.registerLoadEvents();
    this.loadAssets();
  }

  create(): void {
    // Clean up loading UI
    this.destroyLoadingUI();

    // Hide HTML loading splash
    if (typeof document !== 'undefined') {
      const splash = document.getElementById('loading-splash');
      if (splash) {
        splash.style.opacity = '0';
        setTimeout(() => splash.remove(), 500);
      }
    }

    // Transition to MapSelectScene
    this.scene.start('MapSelectScene');
  }

  // --- Loading UI ---

  /**
   * Creates the child-friendly loading screen with:
   * - Colorful background gradient
   * - Rounded progress bar with border
   * - Animated star decorations
   * - Friendly loading text in pt-PT
   */
  private createLoadingUI(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;

    // Background color
    this.cameras.main.setBackgroundColor('#1a1a3e');

    // Loading text (above bar)
    this.loadingText = this.add
      .text(centerX, centerY - 60, 'A preparar a aventura...', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '28px',
        color: COLORS.text,
        align: 'center',
      })
      .setOrigin(0.5);

    // Progress bar background (rounded rectangle with border)
    this.progressBarBg = this.add.graphics();
    this.drawBarBackground(centerX, centerY);

    // Progress bar fill (will be updated on progress events)
    this.progressBar = this.add.graphics();

    // Percentage text (inside bar)
    this.percentText = this.add
      .text(centerX, centerY, '0%', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '18px',
        color: COLORS.text,
        align: 'center',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // Decorative stars along the bar path
    this.createStars(centerX, centerY);
  }

  /**
   * Draws the progress bar background with rounded corners and border.
   */
  private drawBarBackground(centerX: number, centerY: number): void {
    if (!this.progressBarBg) return;

    const { barWidth, barHeight, barRadius, borderWidth } = LAYOUT;
    const x = centerX - barWidth / 2;
    const y = centerY - barHeight / 2;

    // Border
    this.progressBarBg.fillStyle(COLORS.barBorder, 1);
    this.progressBarBg.fillRoundedRect(
      x - borderWidth,
      y - borderWidth,
      barWidth + borderWidth * 2,
      barHeight + borderWidth * 2,
      barRadius + borderWidth
    );

    // Inner background
    this.progressBarBg.fillStyle(COLORS.barBackground, 1);
    this.progressBarBg.fillRoundedRect(x, y, barWidth, barHeight, barRadius);
  }

  /**
   * Creates decorative star graphics that light up as loading progresses.
   */
  private createStars(centerX: number, centerY: number): void {
    const { barWidth, starCount, starSize } = LAYOUT;
    const startX = centerX - barWidth / 2;
    const spacing = barWidth / (starCount + 1);

    for (let i = 0; i < starCount; i++) {
      const star = this.add.graphics();
      const sx = startX + spacing * (i + 1);
      const sy = centerY + 50;

      // Draw a simple star shape (dimmed initially)
      this.drawStar(star, sx, sy, starSize, COLORS.starColor, 0.3);
      this.stars.push(star);
    }
  }

  /**
   * Draws a 5-pointed star at the given position.
   */
  private drawStar(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    size: number,
    color: number,
    alpha: number
  ): void {
    graphics.clear();
    graphics.fillStyle(color, alpha);

    const points: Phaser.Math.Vector2[] = [];
    const outerRadius = size;
    const innerRadius = size * 0.4;

    for (let i = 0; i < 10; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (Math.PI / 5) * i - Math.PI / 2;
      points.push(
        new Phaser.Math.Vector2(
          x + Math.cos(angle) * radius,
          y + Math.sin(angle) * radius
        )
      );
    }

    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fillPath();
  }

  // --- Load events ---

  /**
   * Registers Phaser loader events to update the progress bar.
   */
  private registerLoadEvents(): void {
    this.load.on('progress', (value: number) => {
      this.updateProgressBar(value);
      // Update HTML splash bar
      if (typeof document !== 'undefined') {
        const bar = document.getElementById('splash-bar');
        if (bar) bar.style.width = `${Math.round(value * 100)}%`;
      }
    });

    this.load.on('fileprogress', (_file: Phaser.Loader.File) => {
      // Could be used for per-file feedback if needed
    });

    this.load.on('complete', () => {
      this.updateProgressBar(1);
    });
  }

  /**
   * Updates the progress bar fill and star decorations based on load progress.
   */
  private updateProgressBar(value: number): void {
    if (!this.progressBar) return;

    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;
    const { barWidth, barHeight, barRadius } = LAYOUT;

    const x = centerX - barWidth / 2;
    const y = centerY - barHeight / 2;
    const fillWidth = barWidth * value;

    // Redraw progress fill
    this.progressBar.clear();

    if (fillWidth > 0) {
      // Main fill color
      this.progressBar.fillStyle(COLORS.barFill, 1);
      this.progressBar.fillRoundedRect(
        x,
        y,
        Math.max(fillWidth, barRadius * 2), // Ensure minimum width for rounded corners
        barHeight,
        barRadius
      );

      // Highlight strip at top for 3D effect
      this.progressBar.fillStyle(COLORS.barFillHighlight, 0.4);
      this.progressBar.fillRoundedRect(
        x + 4,
        y + 4,
        Math.max(fillWidth - 8, barRadius),
        barHeight * 0.3,
        barRadius - 4
      );

      // Clip the fill to not exceed the bar bounds
      // (Phaser rounded rect handles this naturally when fillWidth < barWidth)
    }

    // Update percentage text
    if (this.percentText) {
      this.percentText.setText(`${Math.round(value * 100)}%`);
    }

    // Light up stars based on progress
    this.updateStars(value);
  }

  /**
   * Lights up stars progressively as loading advances.
   */
  private updateStars(progress: number): void {
    const { barWidth, starCount, starSize } = LAYOUT;
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;
    const startX = centerX - barWidth / 2;
    const spacing = barWidth / (starCount + 1);

    for (let i = 0; i < this.stars.length; i++) {
      const threshold = (i + 1) / (starCount + 1);
      const isLit = progress >= threshold;
      const sx = startX + spacing * (i + 1);
      const sy = centerY + 50;

      this.drawStar(
        this.stars[i],
        sx,
        sy,
        isLit ? starSize * 1.2 : starSize,
        COLORS.starColor,
        isLit ? 1.0 : 0.3
      );
    }
  }

  // --- Asset loading ---

  /**
   * Loads all assets defined in the boot manifest.
   * Assets are kept minimal to meet the 3s load target on 10 Mbps.
   */
  private loadAssets(): void {
    // Load images
    for (const img of this.manifest.images) {
      this.load.image(img.key, img.path);
    }

    // Load spritesheets
    for (const sheet of this.manifest.spritesheets) {
      this.load.spritesheet(sheet.key, sheet.path, {
        frameWidth: sheet.frameWidth,
        frameHeight: sheet.frameHeight,
      });
    }

    // Load audio
    for (const audio of this.manifest.audio) {
      this.load.audio(audio.key, audio.path);
    }
  }

  // --- Cleanup ---

  /**
   * Destroys all loading UI elements before transitioning.
   */
  private destroyLoadingUI(): void {
    this.progressBar?.destroy();
    this.progressBarBg?.destroy();
    this.loadingText?.destroy();
    this.percentText?.destroy();
    this.stars.forEach((star) => star.destroy());

    this.progressBar = null;
    this.progressBarBg = null;
    this.loadingText = null;
    this.percentText = null;
    this.stars = [];
  }
}
